import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Check, X, Calendar } from 'lucide-react';

const timeSlots = [
  '09:00', '11:00', '13:00', '15:00', '17:00'
];

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  return { daysInMonth, startingDayOfWeek };
};

export const CalendlyCalendar = ({ selectedSlots, onSelectSlot, maxSlots }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState('');

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  };

  const formatTime = (time) => {
    const hour = parseInt(time.split(':')[0]);
    return `${time} - ${hour + 2}:00`;
  };

  const removeSlot = (slotKey) => {
    onSelectSlot(slotKey);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">Select Your Time Slots</h2>
      <p className="text-center text-sm text-foreground-muted mb-8">
        You must book {maxSlots} slot(s) of 2h • {selectedSlots.length}/{maxSlots} selected
      </p>

      {/* Selected Slots Summary */}
      {selectedSlots.length > 0 && (
        <div className="mb-8 p-6 bg-primary/5 border border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-primary">Selected Slots</h3>
            <span className="text-sm font-bold text-primary">{selectedSlots.length}/{maxSlots}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedSlots.map((slot, index) => {
              const [date, time] = slot.split('_');
              return (
                <div
                  key={slot}
                  className="flex items-center justify-between p-3 bg-background border border-border hover:border-primary transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-sm font-semibold">{formatDate(date)}</p>
                      <p className="text-xs text-foreground-muted">{formatTime(time)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeSlot(slot)}
                    className="p-1 hover:bg-primary/10 transition-colors"
                    data-testid={`remove-slot-${index}`}
                  >
                    <X className="w-4 h-4 text-foreground-muted hover:text-primary" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calendar */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-2 hover:bg-primary/10 transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-heading font-semibold capitalize">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-2 hover:bg-primary/10 transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-2 text-center text-sm mb-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="text-foreground-muted font-semibold">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {(() => {
              const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
              const days = [];
              
              for (let i = 0; i < (startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1); i++) {
                days.push(<div key={`empty-${i}`} />);
              }
              
              for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                const dateStr = date.toISOString().split('T')[0];
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                const isSelected = selectedDate === dateStr;
                const hasSlots = selectedSlots.some(slot => slot.startsWith(dateStr));
                
                days.push(
                  <button
                    key={day}
                    onClick={() => !isPast && setSelectedDate(dateStr)}
                    disabled={isPast}
                    className={`aspect-square p-2 text-sm transition-all relative ${
                      isPast
                        ? 'text-foreground-muted/30 cursor-not-allowed'
                        : isSelected
                        ? 'bg-primary text-primary-foreground font-bold'
                        : isToday
                        ? 'border-2 border-primary text-primary'
                        : 'hover:bg-primary/10'
                    }`}
                  >
                    {day}
                    {hasSlots && !isSelected && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                    )}
                  </button>
                );
              }
              
              return days;
            })()}
          </div>
        </div>

        {/* Time Slots */}
        <div>
          <h3 className="text-xl font-heading font-semibold mb-4">
            Available Slots
          </h3>
          {!selectedDate ? (
            <div className="p-8 border-2 border-dashed border-border text-center">
              <Calendar className="w-12 h-12 text-foreground-muted mx-auto mb-3" />
              <p className="text-sm text-foreground-muted">
                Select a date to view available slots
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {timeSlots.map((time) => {
                const slotKey = `${selectedDate}_${time}`;
                const isSelected = selectedSlots.includes(slotKey);
                const hour = parseInt(time.split(':')[0]);
                
                return (
                  <button
                    key={time}
                    onClick={() => onSelectSlot(slotKey)}
                    className={`w-full p-4 text-left border-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`timeslot-${time}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-semibold">{time} - {hour + 2}:00</p>
                          <p className="text-xs text-foreground-muted">2-hour session</p>
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {selectedDate && (
            <div className="mt-6 p-4 bg-muted border border-border">
              <p className="text-xs text-foreground-muted mb-2">💡 Tip</p>
              <p className="text-sm text-foreground">
                You can select slots on different days for your {maxSlots * 2}h package.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
